// Content-reporting client. Lets users flag offensive AI-generated
// content (images, chat replies, companion replies) from inside the app
// — required by Google Play's Generative AI / AI-Generated Content
// policy, which mandates an in-app way to report or flag offensive
// output.
//
// Best-effort by design: the report is POSTed to our backend when a
// /api/report behavior is available, but the call NEVER throws. Even if
// the network is down or the endpoint isn't deployed yet, the user still
// gets a "thanks, we'll review it" confirmation — the moderation queue
// is a server concern, the in-app affordance is what the policy requires.

import { getToken } from './auth';
import { getAnonUserId } from './localChats';

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');

// The reasons we surface, aligned to the categories app-store reviewers
// care about (sexual content, hate, violence, child safety, etc.).
export const REPORT_REASONS = [
  { id: 'sexual',      label: 'Sexually explicit content' },
  { id: 'hate',        label: 'Hate speech or symbols' },
  { id: 'violence',    label: 'Violence or gore' },
  { id: 'harassment',  label: 'Harassment or bullying' },
  { id: 'child_safety',label: 'Child sexual abuse or endangerment' },
  { id: 'illegal',     label: 'Illegal or dangerous acts' },
  { id: 'other',       label: 'Something else' },
];

/**
 * Submit a content report. Resolves to `true` on a best-effort basis and
 * NEVER rejects — callers can always show a confirmation to the user.
 *
 * @param {object} opts
 * @param {'image'|'chat'|'companion'} opts.kind  Surface the content came from.
 * @param {string} opts.reason   One of REPORT_REASONS[].id.
 * @param {string} [opts.details] Free-text elaboration from the user.
 * @param {string} [opts.content] The offending text/prompt, when available.
 * @param {object} [opts.meta]    Extra context (model, style, batch id…).
 */
export async function reportContent({ kind, reason, details = '', content = '', meta = {} } = {}) {
  const payload = {
    kind,
    reason,
    details: (details || '').slice(0, 2000),
    content: (content || '').slice(0, 4000),
    meta,
    // Client timestamp is advisory; the server stamps its own on receipt.
    reportedAt: new Date().toISOString(),
  };

  try {
    const token = getToken();
    const uid = token ? null : getAnonUserId();
    await fetch(`${API_BASE}/api/report`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token
          ? { Authorization: `Bearer ${token}` }
          : (uid ? { 'X-Anon-Uid': uid } : {})),
      },
      body: JSON.stringify(payload),
      // Don't let a hung request block the UI — the confirmation is
      // shown regardless of what the network does.
      keepalive: true,
    });
  } catch {
    // Swallow — reporting is best-effort. The user still sees a thank-you.
  }
  return true;
}
